import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaFilter, FaStar, FaChevronDown, FaChevronUp, FaStarHalfAlt, FaCheck, FaTimes, FaSort } from "react-icons/fa";
import { FetchNestedCategories, GetProductsSearch, GetSuggestions, FetchFilteredProducts } from "../Data";
import { IMAGES, SEARCH, UI_TEXT } from "../constants";

const SearchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('q') || '';
  
  // Core state
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [selectedPriceRanges, setSelectedPriceRanges] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedRating, setSelectedRating] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortBy, setSortBy] = useState(SEARCH.DEFAULT_SORT);
  const [priceRange, setPriceRange] = useState(SEARCH.PRICE_RANGE_DEFAULT);
  
  // UI states
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    category: true,
    price: true,
    brand: true,
    rating: true,
    availability: true
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(SEARCH.DEFAULT_RESULTS_PER_PAGE);

  // Load search results and categories
  useEffect(() => {
    const loadSearchData = async () => {
      if (!searchQuery.trim()) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const [searchResults, categoriesData] = await Promise.all([
          GetProductsSearch(searchQuery),
          FetchNestedCategories()
        ]);
        
        // Handle different response structures
        let productsData = searchResults;
        if (searchResults?.data) {
          productsData = searchResults.data;
        } else if (Array.isArray(searchResults)) {
          productsData = searchResults;
        } else {
          productsData = [];
        }
        
        console.log('Search Results Raw:', searchResults);
        console.log('Processed Products Data:', productsData);
        console.log('Products Count:', productsData?.length || 0);
        
        setProducts(productsData || []);
        setCategories(categoriesData || []);
        
        // Set initial price range based on products
        if (productsData && productsData.length > 0) {
          const prices = productsData.map(p => {
            const price = parseFloat(p.newPrice || p.price || p.currentPrice || 0);
            console.log('Product price:', p.productName, price);
            return price;
          }).filter(price => price > 0);
          
          console.log('All prices:', prices);
          
          if (prices.length > 0) {
            const minPrice = Math.floor(Math.min(...prices));
            const maxPrice = Math.ceil(Math.max(...prices));
            console.log('Price range calculated:', minPrice, 'to', maxPrice);
            setPriceRange([minPrice, maxPrice]);
          }
        }
        
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to load search results. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadSearchData();
  }, [searchQuery]);

  // Extract available brands and categories from search results
  const availableBrands = useMemo(() => {
    const brands = [...new Set(products.map(p => p.brand).filter(Boolean))];
    return brands.sort();
  }, [products]);

  const availableCategories = useMemo(() => {
    const categoryMap = new Map();
    
    products.forEach(product => {
      // Find the category this product belongs to
      categories.forEach(category => {
        if (category.subCategories) {
          category.subCategories.forEach(subCat => {
            if (subCat.categoryId === product.subCategoryId || 
                subCat.subCategoryId === product.subCategoryId) {
              const key = `${category.categoryId}-${category.categoryName}`;
              if (!categoryMap.has(key)) {
                categoryMap.set(key, {
                  id: category.categoryId,
                  name: category.categoryName,
                  count: 0
                });
              }
              categoryMap.get(key).count++;
            }
          });
        }
      });
    });
    
    return Array.from(categoryMap.values()).sort((a, b) => b.count - a.count);
  }, [products, categories]);

  // Calculate dynamic price ranges based on actual product prices
  const priceRanges = useMemo(() => {
    console.log('Calculating price ranges for', products.length, 'products');
    
    if (!products || products.length === 0) {
      console.log('No products, returning empty price ranges');
      return [];
    }
    
    const prices = products.map(product => {
      const price = parseFloat(product.newPrice || product.price || product.currentPrice || 0);
      return price;
    }).filter(price => price > 0);
    
    console.log('Valid prices found:', prices);
    
    if (prices.length === 0) {
      console.log('No valid prices found');
      return [];
    }
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    
    console.log('Min price:', minPrice, 'Max price:', maxPrice);
    
    if (minPrice === maxPrice) {
      console.log('All products have same price, no ranges needed');
      return [];
    }
    
    // Create 5 dynamic ranges based on price distribution
    const range = maxPrice - minPrice;
    const step = range / 5;
    
    const ranges = [
      { 
        key: 'range-1', 
        label: `Under KSH ${Math.round(minPrice + step).toLocaleString()}`, 
        min: 0, 
        max: minPrice + step 
      },
      { 
        key: 'range-2', 
        label: `KSH ${Math.round(minPrice + step).toLocaleString()} to KSH ${Math.round(minPrice + step * 2).toLocaleString()}`, 
        min: minPrice + step, 
        max: minPrice + step * 2 
      },
      { 
        key: 'range-3', 
        label: `KSH ${Math.round(minPrice + step * 2).toLocaleString()} to KSH ${Math.round(minPrice + step * 3).toLocaleString()}`, 
        min: minPrice + step * 2, 
        max: minPrice + step * 3 
      },
      { 
        key: 'range-4', 
        label: `KSH ${Math.round(minPrice + step * 3).toLocaleString()} to KSH ${Math.round(minPrice + step * 4).toLocaleString()}`, 
        min: minPrice + step * 3, 
        max: minPrice + step * 4 
      },
      { 
        key: 'range-5', 
        label: `KSH ${Math.round(minPrice + step * 4).toLocaleString()} & Above`, 
        min: minPrice + step * 4, 
        max: Infinity 
      }
    ];
    
    console.log('Generated price ranges:', ranges);
    return ranges;
  }, [products]);
  
  // Filter and sort products
  useEffect(() => {
    let filtered = [...products];

    // Price range filters
    if (selectedPriceRanges.length > 0) {
      filtered = filtered.filter(product => {
        const price = parseFloat(product.newPrice || product.price || 0);
        return selectedPriceRanges.some(rangeKey => {
          const range = priceRanges.find(r => r.key === rangeKey);
          if (!range) return false;
          return price >= range.min && price <= range.max;
        });
      });
    } else {
      // Apply custom price range if no checkboxes selected
      filtered = filtered.filter(product => {
        const price = parseFloat(product.newPrice || product.price || 0);
        return price >= priceRange[0] && price <= priceRange[1];
      });
    }

    // Brand filter
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(product => 
        selectedBrands.includes(product.brand)
      );
    }

    // Category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(product => 
        selectedCategories.includes(product.categoryId)
      );
    }

    // Rating filter
    if (selectedRating > 0) {
      filtered = filtered.filter(product => 
        (product.rating || 0) >= selectedRating
      );
    }

    // Stock filter
    if (inStockOnly) {
      filtered = filtered.filter(product => 
        product.stockQuantity > 0
      );
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => parseFloat(a.newPrice || a.price || 0) - parseFloat(b.newPrice || b.price || 0));
        break;
      case 'price-high':
        filtered.sort((a, b) => parseFloat(b.newPrice || b.price || 0) - parseFloat(a.newPrice || a.price || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'popularity':
        filtered.sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
        break;
      default: // relevance
        break;
    }

    setFilteredProducts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [products, selectedPriceRanges, selectedBrands, selectedCategories, selectedRating, inStockOnly, sortBy, priceRange, priceRanges]);

  // Filter handlers
  const handlePriceRangeToggle = useCallback((range) => {
    setSelectedPriceRanges(prev => 
      prev.includes(range) 
        ? prev.filter(r => r !== range)
        : [...prev, range]
    );
  }, []);

  const handleBrandToggle = useCallback((brand) => {
    setSelectedBrands(prev => 
      prev.includes(brand) 
        ? prev.filter(b => b !== brand)
        : [...prev, brand]
    );
  }, []);

  const handleCategoryToggle = useCallback((categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const toggleFilterSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedPriceRanges([]);
    setSelectedBrands([]);
    setSelectedCategories([]);
    setSelectedRating(0);
    setInStockOnly(false);
    setPriceRange(SEARCH.PRICE_RANGE_DEFAULT);
    setSortBy(SEARCH.DEFAULT_SORT);
  }, []);

  // Helper functions
  const getProductImageUrl = (product) => {
    try {
     if (product.imageUrl) {
        return product.imageUrl;
      }
    } catch (error) {
      console.error('Error parsing product images:', error);
    }
    return IMAGES.PLACEHOLDER;
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="text-yellow-400 text-xs" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="text-yellow-400 text-xs" />);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="text-gray-300 text-xs" />);
    }

    return stars;
  };

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + resultsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-400 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">{UI_TEXT.LOADING_SEARCH} for "{searchQuery}"...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-orange-400 text-white px-6 py-2 rounded hover:bg-orange-500 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!searchQuery.trim()) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 font-medium mb-4">Please enter a search term</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-orange-400 text-white px-6 py-2 rounded hover:bg-orange-500 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation Bar - Amazon style */}
      <div className="bg-gray-800 text-white py-2">
        <div className="max-w-screen-xl mx-auto px-4">
          <nav className="text-sm">
            <span className="hover:underline cursor-pointer" onClick={() => navigate('/')}>
              Quickcrate Express
            </span>
            <span className="mx-2">›</span>
            <span className="text-orange-300">
              Search results for "{searchQuery}"
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 py-4">
        {/* Results Header */}
        <div className="mb-4">
          <h1 className="text-xl font-normal mb-1">
            1-{Math.min(resultsPerPage, filteredProducts.length)} of {filteredProducts.length} results for
          </h1>
          <h2 className="text-2xl font-normal text-orange-700">
            "{searchQuery}"
          </h2>
          {products.length !== filteredProducts.length && (
            <div className="mt-2 flex gap-2 flex-wrap">
              <span className="text-sm text-gray-600">Applied filters:</span>
              {selectedPriceRanges.map(range => (
                <span key={range} className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  {range.replace('-', ' - ')}
                  <button 
                    onClick={() => handlePriceRangeToggle(range)}
                    className="ml-1 text-blue-600 hover:text-blue-800"
                  >
                    <FaTimes className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {selectedBrands.map(brand => (
                <span key={brand} className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  {brand}
                  <button 
                    onClick={() => handleBrandToggle(brand)}
                    className="ml-1 text-green-600 hover:text-green-800"
                  >
                    <FaTimes className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {(selectedPriceRanges.length > 0 || selectedBrands.length > 0 || selectedCategories.length > 0 || selectedRating > 0 || inStockOnly) && (
                <button 
                  onClick={clearAllFilters}
                  className="text-sm text-blue-600 hover:text-orange-600 hover:underline"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-4">
          {/* Mobile Filter Button */}
          <button
            onClick={() => setShowMobileFilters(true)}
            className="md:hidden fixed bottom-6 right-6 z-40 bg-orange-400 text-white p-3 rounded-full shadow-lg hover:bg-orange-500 transition-colors"
          >
            <FaFilter className="text-xl" />
          </button>

          {/* Mobile Filter Overlay */}
          {showMobileFilters && (
            <>
              <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={() => setShowMobileFilters(false)}></div>
              <div className="fixed top-0 left-0 h-full w-80 bg-white z-50 shadow-xl overflow-y-auto md:hidden">
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-bold">Filters</h2>
                    <button onClick={() => setShowMobileFilters(false)}>
                      <FaTimes className="text-gray-500" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {/* Mobile filters content - same as desktop */}
                </div>
              </div>
            </>
          )}

          {/* Left Sidebar - Filters (Desktop) */}
          <div className="hidden md:block w-64 flex-shrink-0">
            {/* Category Filter */}
            {availableCategories.length > 0 && (
              <div className="mb-6">
                    <h3 className="font-bold text-sm mb-3 text-gray-900">{UI_TEXT.DEPARTMENT}</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableCategories.slice(0, 8).map(category => (
                    <label key={category.id} className="flex items-center text-sm">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => handleCategoryToggle(category.id)}
                        className="mr-2 text-blue-600"
                      />
                      <span className="text-blue-600 hover:text-orange-600 hover:underline">
                        {category.name} ({category.count})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Price Filter - only show if price ranges exist */}
            {(() => {
              console.log('Rendering price filter. Price ranges:', priceRanges, 'Length:', priceRanges?.length);
              return priceRanges && priceRanges.length > 0;
            })() && (
              <div className="mb-6 pb-4 border-b border-gray-200">
                <h4 className="font-bold text-sm mb-3 text-gray-900">{UI_TEXT.PRICE}</h4>
                <div className="space-y-2">
                  {priceRanges.map(range => {
                    const count = products.filter(product => {
                      const price = parseFloat(product.newPrice || product.price || product.currentPrice || 0);
                      const inRange = price >= range.min && price <= range.max;
                      return inRange;
                    }).length;
                    
                    console.log(`Range ${range.key}: ${range.label} has ${count} products`);
                    
                    if (count === 0) return null;
                    
                    return (
                      <label key={range.key} className="flex items-center text-sm">
                        <input 
                          type="checkbox" 
                          className="mr-2 text-blue-600"
                          checked={selectedPriceRanges.includes(range.key)}
                          onChange={() => handlePriceRangeToggle(range.key)}
                        />
                        <span>{range.label} ({count})</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Brand Filter - only show if brands exist */}
            {availableBrands.length > 0 && (
              <div className="mb-6 pb-4 border-b border-gray-200">
                <h4 className="font-bold text-sm mb-3 text-gray-900">{UI_TEXT.BRAND}</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {availableBrands.slice(0, 10).map(brand => {
                    const brandCount = products.filter(p => p.brand === brand).length;
                    return (
                      <label key={brand} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => handleBrandToggle(brand)}
                          className="mr-2 text-blue-600"
                        />
                        <span className="text-blue-600 hover:text-orange-600 hover:underline">
                          {brand} ({brandCount})
                        </span>
                      </label>
                    );
                  })}
                  {availableBrands.length > 10 && (
                    <div className="text-xs text-gray-600 mt-2">
                      +{availableBrands.length - 10} more brands
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Customer Review - only show if products have ratings */}
            {products.some(p => p.rating && p.rating > 0) && (
              <div className="mb-6 pb-4 border-b border-gray-200">
                <h4 className="font-bold text-sm mb-3 text-gray-900">{UI_TEXT.CUSTOMER_REVIEW}</h4>
                <div className="space-y-2">
                  {[4, 3, 2, 1].map(rating => {
                    const productCount = products.filter(p => (p.rating || 0) >= rating).length;
                    if (productCount === 0) return null;
                    
                    return (
                      <label key={rating} className="flex items-center text-sm cursor-pointer">
                        <input
                          type="radio"
                          name="rating"
                          checked={selectedRating === rating}
                          onChange={() => setSelectedRating(rating)}
                          className="mr-2 text-blue-600"
                        />
                        <div className="flex items-center">
                          <div className="flex mr-1">
                            {renderStars(rating)}
                          </div>
                          <span className="text-blue-600 hover:text-orange-600 hover:underline">
                            & Up ({productCount})
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Availability - only show if there are products */}
            {products.length > 0 && (
              <div className="mb-6">
                <h4 className="font-bold text-sm mb-3 text-gray-900">{UI_TEXT.AVAILABILITY}</h4>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="mr-2 text-blue-600"
                  />
                  <span className="text-blue-600 hover:text-orange-600 hover:underline">
                    In Stock ({products.filter(p => p.stockQuantity > 0).length})
                  </span>
                </label>
              </div>
            )}
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Sort Bar */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-300">
              <div className="flex items-center gap-2">
                <span className="text-gray-700">{UI_TEXT.SORT_BY}:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 px-2 py-1 text-sm rounded"
                >
                  <option value="relevance">Relevance</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  {/* Only show rating sort if products have ratings */}
                  {products.some(product => product.rating && product.rating > 0) && (
                    <option value="rating">Avg. Customer Review</option>
                  )}
                  {/* Only show newest sort if products have creation dates */}
                  {products.some(product => product.createdAt || product.dateAdded) && (
                    <option value="newest">Newest Arrivals</option>
                  )}
                  <option value="popularity">Popularity</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                {startIndex + 1}-{Math.min(startIndex + resultsPerPage, filteredProducts.length)} of {filteredProducts.length} results
              </div>
            </div>

            {/* Products Grid */}
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg mb-4">
                  {UI_TEXT.NO_RESULTS_FOUND} for "{searchQuery}"
                  {selectedPriceRanges.length > 0 || selectedBrands.length > 0 || selectedCategories.length > 0 || selectedRating > 0 || inStockOnly ? ' with the applied filters.' : '.'}
                </p>
                {(selectedPriceRanges.length > 0 || selectedBrands.length > 0 || selectedCategories.length > 0 || selectedRating > 0 || inStockOnly) && (
                  <button
                    onClick={clearAllFilters}
                    className="bg-orange-400 text-white px-6 py-2 rounded hover:bg-orange-500 transition-colors"
                  >
                    {UI_TEXT.CLEAR_FILTERS}
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {paginatedProducts.map(product => (
                    <div
                      key={product.productId}
                      className="border border-gray-200 rounded-lg p-3 hover:shadow-lg transition-shadow bg-white"
                    >
                      <Link 
                        to={`/product/${encodeURIComponent(product.productName || product.description)}/${product.productId}`}
                        className="block"
                      >
                        <div className="aspect-square mb-3 relative">
                          <img
                            src={getProductImageUrl(product)}
                            alt={product.productName || product.description}
                            className="w-full h-full object-contain rounded"
                            onError={(e) => {
                              e.target.src = IMAGES.PLACEHOLDER;
                            }}
                          />
                          {product.oldPrice && (
                            <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                              -{Math.round(((product.oldPrice - (product.newPrice || product.price)) / product.oldPrice) * 100)}%
                            </div>
                          )}
                        </div>
                        
                        <h3 className="text-sm text-blue-600 hover:text-orange-600 mb-2 line-clamp-3 font-normal">
                          {product.description || product.productName}
                        </h3>
                        
                        <div className="flex items-center gap-1 mb-2">
                          <div className="flex">
                            {renderStars(product.rating || 0)}
                          </div>
                          {product.reviewCount && product.reviewCount > 0 && (
                            <span className="text-blue-600 hover:text-orange-600 text-xs">
                              ({product.reviewCount})
                            </span>
                          )}
                        </div>

                        <div className="mb-2">
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-normal">
                              KSH {Number(product.newPrice || product.price || 0).toLocaleString()}
                            </span>
                            {product.oldPrice && (
                              <span className="text-sm text-gray-500 line-through">
                                KSH {Number(product.oldPrice).toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          {product.oldPrice && (
                            <div className="text-xs text-gray-600">
                              Save KSH {Number(product.oldPrice - (product.newPrice || product.price || 0)).toLocaleString()}
                            </div>
                          )}
                        </div>

                        {/* Only show delivery info if available from product data */}
                        {product.deliveryInfo && (
                          <div className="text-xs text-gray-600 mb-2">
                            {product.deliveryInfo}
                          </div>
                        )}

                        <div className={`text-xs font-medium ${
                          product.stockQuantity > 0 ? 'text-green-700' : 'text-red-600'
                        }`}>
                          {product.stockQuantity > 0 
                            ? `In Stock` 
                            : 'Currently unavailable'
                          }
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      
                      {[...Array(Math.min(totalPages, 7))].map((_, index) => {
                        let pageNumber;
                        if (totalPages <= 7) {
                          pageNumber = index + 1;
                        } else if (currentPage <= 4) {
                          pageNumber = index + 1;
                        } else if (currentPage >= totalPages - 3) {
                          pageNumber = totalPages - 6 + index;
                        } else {
                          pageNumber = currentPage - 3 + index;
                        }

                        return (
                          <button
                            key={pageNumber}
                            onClick={() => setCurrentPage(pageNumber)}
                            className={`px-3 py-2 border text-sm rounded ${
                              currentPage === pageNumber
                                ? 'bg-orange-400 text-white border-orange-400'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;